import { WechatApi } from '../api/wechat';

const JWEXIN_SRC = 'https://res.wx.qq.com/open/js/jweixin-1.6.0.js';

/**
 * 注入微信 JSSDK 脚本
 * @returns {Promise<typeof wx>}
 */
export function loadJWeixinScript() {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('no window'));
  }
  if (typeof window.wx !== 'undefined' && window.wx.config) {
    return Promise.resolve(window.wx);
  }
  return new Promise((resolve, reject) => {
    const el = document.createElement('script');
    el.src = JWEXIN_SRC;
    el.async = true;
    el.onload = () => {
      if (typeof window.wx !== 'undefined') resolve(window.wx);
      else reject(new Error('wx not defined'));
    };
    el.onerror = () => reject(new Error('load jweixin failed'));
    document.head.appendChild(el);
  });
}

/**
 * 解析后端返回体（如 `{ success, code, data: { appId, timestamp, nonceStr, signature } }`）
 * @param {object} raw
 * @returns {{ appId: string, timestamp: string|number, nonceStr: string, signature: string }}
 */
function normalizeJsSdkConfig(raw) {
  if (raw && raw.success === false) {
    throw new Error(String(raw.message || raw.error_msg || 'js-sdk sign failed'));
  }
  if (raw != null && raw.code != null && Number(raw.code) !== 200) {
    throw new Error(String(raw.message || raw.error_msg || 'js-sdk sign failed'));
  }
  const o = raw?.data && typeof raw.data === 'object' ? raw.data : raw || {};
  const appId = o.appId || o.appid;
  const timestamp = o.timestamp;
  const nonceStr = o.nonceStr || o.nonce_str;
  const signature = o.signature;
  if (!appId || timestamp === undefined || !nonceStr || !signature) {
    throw new Error('invalid js-sdk sign payload');
  }
  return { appId, timestamp, nonceStr, signature };
}

/**
 * 拉取签名并执行 wx.config（含开放标签 wx-open-launch-app）
 * @param {object} [options]
 * @param {string} [options.url] 请求体里的页面地址；不传时由 `WechatApi.fetchJsSdkSignature` 使用 `location.href`
 * @param {boolean} [options.debug]
 * @returns {Promise<void>}
 */
export async function initWechatJsSdk(options = {}) {
  const res = await WechatApi.fetchJsSdkSignature(options.url);
  const cfg = normalizeJsSdkConfig(res);
  const wx = await loadJWeixinScript();
  const currentHref =
    typeof window !== 'undefined' && window.location ? window.location.href : '';

  const debug = !!options.debug;

  if (typeof document !== 'undefined') {
    document.addEventListener('WeixinOpenTagsError', (e) => {
      console.error('[WeixinOpenTags]', e?.detail?.errMsg || e);
    });
  }

  return new Promise((resolve, reject) => {
    console.log('[wechatJsSdk] wx.config start', {
      href: currentHref,
      appId: cfg.appId,
      timestamp: cfg.timestamp,
      nonceStr: cfg.nonceStr,
      signaturePreview: String(cfg.signature || '').slice(0, 8),
      openTagList: ['wx-open-launch-app'],
    });
    wx.config({
      debug,
      appId: cfg.appId,
      timestamp: cfg.timestamp,
      nonceStr: cfg.nonceStr,
      signature: cfg.signature,
      jsApiList: [],
      openTagList: ['wx-open-launch-app'],
    });
    wx.ready(() => {
      console.log('[wechatJsSdk] wx.config ready');
      resolve();
    });
    wx.error((err) => {
      console.error('[wechatJsSdk] wx.config error', err);
      reject(err || new Error('wx.config error'));
    });
  });
}
